import { useEffect, useState } from "react";
import { useAuthToken } from "../../../../hooks/useAuthToken";

// 🟢 Variable de entorno para la URL del backend
import { safeFetch } from "@/api/safeFetch";
import { buildDocumentUrl } from "@/api/buildImageUrl";

interface Documento {
  id: number;
  nombre: string;
  fecha_creacion: string | null;
  url_pdf: string;
  nombre_empresa: string;
  numero_contrato: number;
  numero_factura: number;
}

const ContratosFacturas: React.FC = () => {
  const [contratos, setContratos] = useState<Documento[]>([]);
  const [facturas, setFacturas] = useState<Documento[]>([]);
  const [loadingContratos, setLoadingContratos] = useState(false);
  const [loadingFacturas, setLoadingFacturas] = useState(false);

  // ✅ Hook que maneja token y usuario
  const { token, usuarioId, error } = useAuthToken();

  // ✅ Cargar documentos cuando tengamos usuario_id y token
  useEffect(() => {
    if (!token || !usuarioId) return;

    const fetchDocumentos = async (
      tipo: "contrato" | "factura",
      setState: React.Dispatch<React.SetStateAction<Documento[]>>,
      setLoading: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      setLoading(true);
      try {
        // 🟢 Llamada a la API usando variable de entorno
        const res = await safeFetch(`/api/documentos?tipo=${tipo}&usuario_id=${usuarioId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
        );

        if (!res.ok) throw new Error(`Error al obtener ${tipo}s`);

        const data: Documento[] = await res.json();
        setState(data || []);
      } catch (err: any) {
        console.error(err);
        setState([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentos("contrato", setContratos, setLoadingContratos);
    fetchDocumentos("factura", setFacturas, setLoadingFacturas);
  }, [token, usuarioId]);

  // Render de error si hay problema con el token
  if (error) {
    return <p className="text-red-600 text-center mt-10">{error}</p>;
  }

  const renderDocumentos = (docs: Documento[], loading: boolean, tipo: string) => {
    if (loading) return <p>Cargando {tipo}s...</p>;
    if (!docs.length) return <p>No hay {tipo}s disponibles.</p>;

    return (
      <div className="flex gap-4"> {/* fila horizontal */}
        {docs.map((doc) => (
          <div
            key={doc.id}
            className="flex-shrink-0 bg-zinc-600 rounded-md p-3 flex flex-col gap-2 w-32 border-4 border-zinc-300 shadow-sm hover:border-blue-600 transition-colors duration-700 delay-200 shadow-blue-700"
          >
            <h4
              className="text-xs font-semibold text-white truncate"
              title={doc.nombre_empresa}
            >
              {doc.nombre_empresa}
            </h4>

            <p className="text-xs text-white">
              {doc.fecha_creacion
                ? new Date(doc.fecha_creacion).toLocaleDateString()
                : "Sin fecha"}
            </p>

            {(tipo === "contrato" ? doc.numero_contrato : doc.numero_factura) && (
              <p className="text-xs text-white font-medium">
                {tipo === "contrato"
                  ? `${doc.numero_contrato}`
                  : `${doc.numero_factura}`}
              </p>
            )}

            {/* 🟢 URL del PDF usando variable de entorno */}
            <a
              href={buildDocumentUrl(doc.url_pdf)} // ✅ Para PDFs
              target="_blank"
              rel="noopener noreferrer"
              className="mt-auto flex items-center justify-center h-8 w-full bg-white hover:bg-blue-700 text-blue-700 hover:text-white border-2 border-blue-700 hover:border-white font-semibold rounded-md py-2"
            >
              Ver
            </a>

          </div>
        ))
        }
      </div >
    );
  };

  return (
    <main className="flex-1 flex items-center justify-center px-4 gap-6 w-screen text-zinc-800">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-7xl px-4 md:px-6">
        {/* Contratos */}
        <div className="bg-white rounded-xl shadow-md p-4 border-t-2 border-r-4 border-zinc-400 hover:border-blue-600 ">
          <h2 className="text-xl font-bold mb-4">Contratos:</h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {renderDocumentos(contratos, loadingContratos, "contrato")}
          </div>
        </div>

        {/* Facturas */}
        <div className="bg-white rounded-xl shadow-md p-4 border-t-2 border-r-4 border-zinc-400 hover:border-blue-600 ">
          <h2 className="text-xl font-bold mb-4">Facturas:</h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {renderDocumentos(facturas, loadingFacturas, "factura")}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ContratosFacturas;
